import React, { memo, useContext } from 'react'
import type { CellValue, IdType } from 'react-table'
import { useIntl } from 'react-intl'

import { useAppSelector } from 'app/hooks'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import { domainNestingEnabledSelector } from 'features/auth/permissions/selectors'
import { MappedDomainUser } from 'features/team/types'
import AvatarBlock from '../AvatarBlock'
import { TEAM_HEADERS, UPDATE_PERMISSIONS_ACTIONS } from 'features/team/consts'
import DeleteButton from '../DeleteButton'
import PermissionCheckbox from '../PermissionCheckbox'
import { teamPageMessages } from 'features/team/messages'
import {
  TeamBlockRow,
  TeamLeftBlock,
  TeamPermission,
  TeamPermissionLabel,
  TeamPermissionsBlockContainer,
  TeamPermissionsWrapper,
  TeamRightBlock,
  TeamUserBlock,
  TeamUserEmail,
  TeamUserName,
} from './styles'

interface TeamPermissionsBlockProps {
  values: Record<IdType<MappedDomainUser>, CellValue>
}

export function TeamPermissionsBlock({ values }: TeamPermissionsBlockProps) {
  const { formatMessage } = useIntl()
  const hasDomainNesting = useAppSelector(domainNestingEnabledSelector)
  const { hasDomainAdminPermission } = useContext(CurrentUserPermissionsContext)

  return (
    <TeamPermissionsBlockContainer>
      <TeamBlockRow>
        <TeamLeftBlock>
          <AvatarBlock url={values[TEAM_HEADERS.AVATAR]} />
          <TeamUserBlock>
            <TeamUserName>{values[TEAM_HEADERS.NAME]}</TeamUserName>
            <TeamUserEmail>{values[TEAM_HEADERS.EMAIL]}</TeamUserEmail>
          </TeamUserBlock>
        </TeamLeftBlock>

        <TeamRightBlock>
          {hasDomainAdminPermission && (
            <DeleteButton
              id={values[TEAM_HEADERS.ID]}
              email={values[TEAM_HEADERS.EMAIL]}
            />
          )}
        </TeamRightBlock>
      </TeamBlockRow>

      <TeamBlockRow>
        <TeamPermissionsWrapper>
          <TeamPermission>
            <TeamPermissionLabel>
              {formatMessage(teamPageMessages.adminColumn)}
            </TeamPermissionLabel>

            <PermissionCheckbox
              updatePermissionsAction={UPDATE_PERMISSIONS_ACTIONS.UPDATE_ADMIN}
              id={values[TEAM_HEADERS.ID]}
              isAdmin={values[TEAM_HEADERS.IS_ADMIN]}
              isConfigAuthor={values[TEAM_HEADERS.IS_CONFIG_AUTHOR]}
              isSubscriptionManager={
                values[TEAM_HEADERS.IS_SUBSCRIPTION_MANAGER] || false
              }
              disabled={!hasDomainAdminPermission}
            />
          </TeamPermission>

          {hasDomainNesting && (
            <TeamPermission>
              <TeamPermissionLabel>
                {formatMessage(teamPageMessages.configColumn)}
              </TeamPermissionLabel>

              <PermissionCheckbox
                updatePermissionsAction={
                  UPDATE_PERMISSIONS_ACTIONS.UPDATE_CONFIG
                }
                id={values[TEAM_HEADERS.ID]}
                isAdmin={values[TEAM_HEADERS.IS_ADMIN]}
                isConfigAuthor={values[TEAM_HEADERS.IS_CONFIG_AUTHOR]}
                isSubscriptionManager={
                  values[TEAM_HEADERS.IS_SUBSCRIPTION_MANAGER] || false
                }
                disabled={!hasDomainAdminPermission}
              />
            </TeamPermission>
          )}

          <TeamPermission>
            <TeamPermissionLabel>
              {formatMessage(teamPageMessages.subscriptionManagementColumn)}
            </TeamPermissionLabel>

            <PermissionCheckbox
              updatePermissionsAction={
                UPDATE_PERMISSIONS_ACTIONS.UPDATE_SUBSCRIPTION_MANAGEMENT
              }
              id={values[TEAM_HEADERS.ID]}
              isAdmin={values[TEAM_HEADERS.IS_ADMIN]}
              isConfigAuthor={values[TEAM_HEADERS.IS_CONFIG_AUTHOR]}
              isSubscriptionManager={
                values[TEAM_HEADERS.IS_SUBSCRIPTION_MANAGER] || false
              }
              disabled={!hasDomainAdminPermission}
              tooltipText="Subscription Management permissions can only be changed in the Savant Community."
            />
          </TeamPermission>
        </TeamPermissionsWrapper>
      </TeamBlockRow>
    </TeamPermissionsBlockContainer>
  )
}

export default memo(TeamPermissionsBlock)
